import { forwardRef, useEffect, useState } from 'react'

import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import dayjs from 'dayjs'

import { DATE_LOCALES, ILocales } from 'shared/constants/date-locale'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { addDate, dateFormat } from 'shared/lib/dateFormat'
import { FloatingInput } from 'shared/ui/date-picker-label/components/floating-input'

import { AppIcon } from '../app-icon'
import { AllIcons } from '../app-icon/types'

import { DatePickerProps } from './model.ts'
import s from './styles.module.scss'

export const DatePickerWithLabel = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      value,
      label,
      onChange,
      rightIcon,
      error,
      minDate,
      maxDate,
      rangeYear = 100,
      readOnly = false,
      format = DATE_FORMAT_LIST.FULL_DATE_DATE_FNS,
      variant = 'default',
    },
    ref,
  ) => {
    const CURRENT_DATE = new Date()

    const MIN_DATE =
      minDate ||
      addDate({
        count: rangeYear * -1,
        type: 'year',
      })

    const { t, i18n } = useTranslation()
    const locale = DATE_LOCALES[i18n.language as ILocales]

    const [selected, setSelected] = useState<Date | null | undefined>(value)
    const [currentMonth, setCurrentMonth] = useState(
      value ? value.getMonth() : new Date().getMonth(),
    )
    const valueTimestamp = value?.getTime()

    useEffect(() => {
      const nextValue = valueTimestamp ? new Date(valueTimestamp) : undefined

      setSelected(nextValue)

      if (nextValue) {
        setCurrentMonth(nextValue.getMonth())
      }
    }, [valueTimestamp])

    const months = [
      t('january'),
      t('february'),
      t('march'),
      t('april'),
      t('may'),
      t('june'),
      t('july'),
      t('august'),
      t('september'),
      t('october'),
      t('november'),
      t('december'),
    ]

    const icon =
      variant === 'default' ? AllIcons.CALENDAR : AllIcons.CALENDAR_NEW

    const onChangeHandler = (date?: Date | null) => {
      if (date && dayjs(date).isValid()) {
        setSelected(date)
        onChange?.(date)
      }
    }

    const headerDate = ({
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }: ReactDatePickerCustomHeaderProps) => {
      return (
        <div className={s.headerPicker}>
          <button
            type="button"
            className={clsx(
              s.rotation,
              s.arrowBtn,
              prevMonthButtonDisabled ? s.arrowBtnDisabled : s.arrowActive,
            )}
            onClick={() => !prevMonthButtonDisabled && decreaseMonth()}
          >
            <AppIcon
              id={AllIcons.ARROW_RIGHT}
              className={s.icon}
            />
          </button>

          <div className={s.head}>
            <select
              value={date.getFullYear()}
              onChange={(e) => changeYear(+e.target.value)}
              className={s.select}
            >
              {new Array(rangeYear + 1).fill('').map((_, index) => {
                const year = addDate({
                  date: MIN_DATE,
                  type: 'year',
                  count: index,
                }).getFullYear()

                return (
                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>
                )
              })}
            </select>

            <select
              value={months[date.getMonth()]}
              onChange={(e) => changeMonth(months.indexOf(e.target.value))}
              className={s.select}
            >
              {months.map((m) => (
                <option
                  key={m}
                  value={m}
                >
                  {m}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={clsx(
              s.arrowBtn,
              nextMonthButtonDisabled ? s.arrowBtnDisabled : s.arrowActive,
            )}
            onClick={() => !nextMonthButtonDisabled && increaseMonth()}
          >
            <AppIcon
              id={AllIcons.ARROW_RIGHT}
              className={s.icon}
            />
          </button>
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={s.root}
      >
        <DatePicker
          withPortal
          showPopperArrow={false}
          locale={locale}
          readOnly={readOnly}
          minDate={MIN_DATE}
          dateFormat={format}
          selected={selected}
          maxDate={maxDate || CURRENT_DATE}
          renderCustomHeader={headerDate}
          onChange={onChangeHandler}
          filterDate={(d) => d.getMonth() === currentMonth}
          onMonthChange={(d) => setCurrentMonth(d.getMonth())}
          customInput={
            <FloatingInput
              label={label}
              value={
                selected
                  ? dateFormat(
                      selected,
                      DATE_FORMAT_LIST.FULL_DATE_WITH_MONTH_STAND_ALONE,
                    )
                  : ''
              }
              error={!!error}
              variant={variant}
              rightIcon={
                rightIcon || (
                  <AppIcon
                    id={icon}
                    height={24}
                    width={24}
                    className={s.iconCalendar}
                  />
                )
              }
            />
          }
        />

        {typeof error !== 'boolean' && error?.message && (
          <p className={clsx(s.errorText, 'medium-12')}>{error.message}</p>
        )}
      </div>
    )
  },
)

DatePickerWithLabel.displayName = 'DatePickerWithLabel'
